import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CheckCircle, X, RotateCcw, ImageIcon } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(seededRandom(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const CLICK_FEEDBACK_MS = 550;

const FindObjectElement = ({
  element,
  isEditor = false,
  studentView = false,
}) => {
  const { t } = useTranslation();
  const { content, id } = element;
  const {
    imageUrl = '',
    hotspots = [],
    shuffleTargets = true,
    allowRetry = false,
    cropRect = null,
    fontFamily = 'Inter',
    fontSize = 22,
    textColor = '#ffffff',
    wordBgColor = '#2A4D25',
  } = content || {};

  const hasCrop = cropRect && cropRect.w > 0 && cropRect.h > 0 &&
    !(cropRect.x === 0 && cropRect.y === 0 && cropRect.w === 100 && cropRect.h === 100);

  const validHotspots = useMemo(
    () => hotspots.filter(
      (h) => (h.word || '').trim() && typeof h.x === 'number' && typeof h.y === 'number'
    ),
    [hotspots]
  );

  // Group hotspots by normalized word. Duplicate words (e.g. teacher labelled
  // two hotspots "cat" — same object drawn twice, or just a typo) collapse to a
  // single prompt; clicking any hotspot in the group counts as correct.
  const wordGroups = useMemo(() => {
    const map = new Map();
    validHotspots.forEach((h) => {
      const key = (h.word || '').trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, { word: h.word.trim(), ids: [] });
      map.get(key).ids.push(h.id);
    });
    return Array.from(map.values());
  }, [validHotspots]);

  const order = useMemo(() => {
    const words = wordGroups.map((g) => g.word);
    if (isEditor || !shuffleTargets) return words;
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(words, seed);
  }, [wordGroups, shuffleTargets, id, isEditor]);

  const [idx, setIdx] = useState(0);
  const [clickMark, setClickMark] = useState(null); // { x, y, kind }
  const clickTimer = useRef(null);

  const orderKey = order.join('|');
  useEffect(() => {
    setIdx(0);
    setClickMark(null);
  }, [orderKey]);

  useEffect(() => () => clearTimeout(clickTimer.current), []);

  const currentWord = order[idx];
  const currentGroup = wordGroups.find((g) => g.word === currentWord) || null;
  const done = !isEditor && order.length > 0 && idx >= order.length;

  const [naturalSize, setNaturalSize] = useState(null);
  const [containerSize, setContainerSize] = useState(null);
  const containerRef = useRef(null);
  const loaderRef = useRef(null);

  useEffect(() => { setNaturalSize(null); }, [imageUrl]);

  useEffect(() => {
    const img = loaderRef.current;
    if (img && img.complete && img.naturalWidth && img.naturalHeight) {
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, [imageUrl]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const apply = (w, h) => {
      if (w > 0 && h > 0) setContainerSize({ w, h });
    };
    apply(el.offsetWidth, el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect.width, e.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [imageUrl]);

  // Painted box: where the (possibly-cropped) image is actually rendered inside
  // the image-area. Hotspots and click coordinates are relative to this box so
  // targeting stays aligned when the widget or the slide is resized.
  const paintedBox = useMemo(() => {
    if (!containerSize || !naturalSize) return null;
    const cropW = hasCrop ? cropRect.w : 100;
    const cropH = hasCrop ? cropRect.h : 100;
    const visibleAspect = (naturalSize.w * cropW) / (naturalSize.h * cropH);
    if (!isFinite(visibleAspect) || visibleAspect <= 0) return null;
    const containerAspect = containerSize.w / containerSize.h;
    let w, h;
    if (visibleAspect > containerAspect) {
      w = containerSize.w;
      h = w / visibleAspect;
    } else {
      h = containerSize.h;
      w = h * visibleAspect;
    }
    return {
      w, h,
      left: (containerSize.w - w) / 2,
      top: (containerSize.h - h) / 2,
    };
  }, [containerSize, naturalSize, hasCrop, cropRect]);

  const handleImageClick = useCallback((e) => {
    if (isEditor || done || !currentGroup || !paintedBox || clickMark) return;

    // Coordinates relative to the painted image box (so crop/letterboxing don't skew targeting).
    const box = e.currentTarget.getBoundingClientRect();
    const cxPct = ((e.clientX - box.left) / box.width) * 100;
    const cyPct = ((e.clientY - box.top) / box.height) * 100;
    const clickPxX = (cxPct / 100) * box.width;
    const clickPxY = (cyPct / 100) * box.height;

    // Any hotspot in the current group is a valid target for this prompt.
    const isHit = currentGroup.ids.some((hsId) => {
      const h = validHotspots.find((v) => v.id === hsId);
      if (!h) return false;
      const targetPxX = (h.x / 100) * box.width;
      const targetPxY = (h.y / 100) * box.height;
      const radiusPx = ((h.radius || 8) / 100) * box.width;
      return Math.hypot(clickPxX - targetPxX, clickPxY - targetPxY) <= radiusPx;
    });

    if (isHit) {
      setClickMark({ x: cxPct, y: cyPct, kind: 'correct' });
      clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => {
        setClickMark(null);
        setIdx((i) => i + 1);
      }, CLICK_FEEDBACK_MS);
    } else {
      setClickMark({ x: cxPct, y: cyPct, kind: 'wrong' });
      clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => setClickMark(null), CLICK_FEEDBACK_MS);
    }
  }, [isEditor, done, currentGroup, paintedBox, clickMark, validHotspots]);

  const handleRetry = () => {
    setIdx(0);
    setClickMark(null);
  };

  // Empty state (editor with no image yet)
  if (!imageUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300">
        <ImageIcon size={40} className="text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">{t('games.dragLabel.uploadImageInSettings')}</p>
      </div>
    );
  }

  const hideChrome = !isEditor;
  const promptWord = isEditor
    ? (wordGroups[0]?.word || t('games.findObjectPlayer.find'))
    : (currentWord || '');

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{ borderColor: '#e5e7eb' }}
    >
      <style>{`
        @keyframes fo-mark-in {
          0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          60% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes fo-word-pop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .fo-mark { animation: fo-mark-in 0.35s cubic-bezier(.2,.8,.2,1.4); }
        .fo-word { animation: fo-word-pop 0.35s cubic-bezier(.2,.8,.2,1.4); }
      `}</style>

      {/* Prompt bar. Kept in the layout even when done so removing it doesn't
          shift the image below on completion. */}
      <div
        className="flex items-center justify-center px-3 py-2 bg-gray-50/80"
        style={{ minHeight: 56 }}
      >
        {!done && promptWord && (
          <div
            key={currentWord || 'editor-preview'}
            className={`fo-word inline-block px-4 py-1 rounded-full font-bold ${
              wordBgColor === 'transparent' ? '' : 'shadow-md'
            }`}
            style={{
              backgroundColor: wordBgColor,
              color: textColor,
              fontFamily,
              fontSize: `${fontSize}px`,
            }}
          >
            {promptWord}
          </div>
        )}
        {done && (
          <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
            <CheckCircle size={16} />
            {t('games.findObjectPlayer.allFound', { defaultValue: 'Усі знайдено!' })}
          </div>
        )}
      </div>

      {/* Image area. Base image is always visible via object-contain (also seeds
          naturalSize via onLoad). The interactive overlay is pinned to the
          painted box on top for accurate click hit-testing and hotspot markers. */}
      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 min-w-0 overflow-hidden"
      >
        {/* Base image — hidden when cropped so the cropped overlay is what's seen. */}
        <img
          ref={loaderRef}
          src={getMediaUrl(imageUrl)}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth;
            const h = e.currentTarget.naturalHeight;
            if (w && h) setNaturalSize({ w, h });
          }}
          className="absolute inset-0 w-full h-full pointer-events-none select-none"
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
            opacity: hasCrop ? 0 : 1,
          }}
        />

        {paintedBox && (
          <div
            onClick={handleImageClick}
            className="absolute overflow-hidden select-none"
            style={{
              left: `${paintedBox.left}px`,
              top: `${paintedBox.top}px`,
              width: `${paintedBox.w}px`,
              height: `${paintedBox.h}px`,
              cursor: !isEditor && !done && currentGroup ? 'crosshair' : 'default',
            }}
          >
            {hasCrop && (
              <img
                src={getMediaUrl(imageUrl)}
                alt=""
                draggable={false}
                className="pointer-events-none select-none"
                style={{
                  position: 'absolute',
                  width: `${10000 / cropRect.w}%`,
                  height: `${10000 / cropRect.h}%`,
                  left: `${-cropRect.x * 100 / cropRect.w}%`,
                  top: `${-cropRect.y * 100 / cropRect.h}%`,
                  maxWidth: 'none',
                }}
              />
            )}

            {clickMark && (
              <div
                className={`fo-mark absolute pointer-events-none flex items-center justify-center rounded-full text-white shadow-lg ${
                  clickMark.kind === 'correct' ? 'bg-emerald-500' : 'bg-red-500'
                }`}
                style={{
                  left: `${clickMark.x}%`,
                  top: `${clickMark.y}%`,
                  width: 40,
                  height: 40,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {clickMark.kind === 'correct' ? <CheckCircle size={22} /> : <X size={22} />}
              </div>
            )}
          </div>
        )}

        {/* Action bar — retry only, once complete */}
        {!isEditor && done && allowRetry && (
          <div className="absolute bottom-2 right-2 z-10">
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindObjectElement;
